/**
 * @module routes.PublicCalendar.NewSession.openOnCalendar
 *
 * Whether a public coach link opens on the calendar or on the coach's sessions.
 *
 * CFTB-3448: a `sessionId` in the URL used to be enough for the calendar, so a link
 * copied off someone's screen — or left over from a build that deep-linked into
 * it — dropped a first-time visitor into a slot list for a session they had never
 * chosen. The parameter stays, because the flows that do settle a session before
 * coming here rely on it and on the back button it gives them (CFTB-3430); what it
 * no longer does is speak for the visitor.
 *
 * Two things count as having settled it, and both are things this browser did:
 *
 *   - `sessionPicked` on the history entry — a session tapped on the coach's page,
 *     or a slot on the facility calendar (CFTB-3439). It travels with the entry, so
 *     a reload keeps the calendar while the back button still returns to the
 *     sessions.
 *   - a chosen time belonging to that same session — the slot survives a sign-in
 *     round trip in persisted state where the history entry does not, so someone
 *     signing in mid-booking comes back to their slot rather than to the list.
 *
 * A cold visit has neither.
 */

export interface CalendarEntry {
  /** `?sessionId=` — the session the URL names, if any. */
  sessionId?: string | null;
  /** `sessionPicked` from the history entry's state. */
  sessionPicked?: boolean;
  /** `bookSession.time` — set only once a slot has been chosen. */
  chosenTime?: unknown;
  /** `bookSession.selectedSession?.id` — which session that slot belongs to. */
  selectedSessionId?: number | string | null;
}

export const openOnCalendar = ({
  sessionId,
  sessionPicked,
  chosenTime,
  selectedSessionId,
}: CalendarEntry): boolean => {
  if (!sessionId) {
    return false;
  }
  if (sessionPicked) {
    return true;
  }
  return (
    Boolean(chosenTime) &&
    String(selectedSessionId ?? "") === String(sessionId)
  );
};

export default openOnCalendar;
